import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { defer, EMPTY, Observable, of } from 'rxjs';
import { concatMap, filter, take, tap } from 'rxjs/operators';
import { API_ROUTE } from '@constants/api.routes.constants';
import { ERRORS } from '@constants/errors.constant';
import { ROUTE, STORAGE_KEY } from '@constants/index';
import { Authentication, authenticationDataBuilder } from '@models/index';
import { ErrorService } from '@services/error/error.service';
import { StorageService } from '@services/storage/storage.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
    }),
  };

  public constructor(
    private readonly http: HttpClient,
    private readonly storageService: StorageService,
    private readonly router: Router,
    private readonly errorService: ErrorService
  ) {}

  public static getTokenFromUrlSearchParams(): string {
    const paramsFromURL = new URLSearchParams(window.location.search);
    const token = paramsFromURL.get('token');
    return token;
  }

  public static getSsoErrorFromUrlSearchParams(): string {
    const paramsFromURL = new URLSearchParams(window.location.search);
    return paramsFromURL.get('error');
  }

  public static removeSsoParamsFromUrl(): void {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('token') && !url.searchParams.has('error')) {
      return;
    }

    url.searchParams.delete('token');
    url.searchParams.delete('error');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  }

  private removeSsoParamsFromRouterUrl(): void {
    const removeParams = (): void => {
      this.router.navigate([], {
        queryParams: { token: null, error: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    };

    if (this.router.navigated) {
      removeParams();
      return;
    }

    this.router.events
      .pipe(
        filter((event): boolean => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe(removeParams);
  }

  public static navigateToBbvaSso(): void {
    window.location.href = API_ROUTE.LOGIN_SAML;
  }

  public isAuthenticated(): boolean {
    return this.storageService.check(STORAGE_KEY.TOKEN);
  }

  // eslint-disable-next-line class-methods-use-this
  public inMaintenance(): boolean {
    return environment.inMaintenance;
  }

  // eslint-disable-next-line class-methods-use-this
  public redirectToSSOLogin(): void {
    if (environment.production) {
      AuthenticationService.navigateToBbvaSso();
    } else {
      this.router.navigate([`/${ROUTE.EMPLOYEE_CHOOSER}`]);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private buildLoginParams(token: string, employeeId: string): Authentication {
    const bypassedToken = environment.production ? token : 'ourSecretToken';

    return {
      token: bypassedToken,
      employeeId,
    };
  }

  public authenticateEmployee(): Observable<void> {
    const currentEmployeeId: string =
      this.storageService.get('employeeId') ||
      localStorage.getItem('employeeId');

    const ssoParams$ = defer(() => {
      const token = AuthenticationService.getTokenFromUrlSearchParams();
      const ssoError = AuthenticationService.getSsoErrorFromUrlSearchParams();
      if (token || ssoError) {
        AuthenticationService.removeSsoParamsFromUrl();
        this.removeSsoParamsFromRouterUrl();
      }
      return of({ token, ssoError });
    });

    return ssoParams$.pipe(
      concatMap(({ token, ssoError }): Observable<any> => {
        if (environment.production && !token) {
          if (ssoError) {
            this.errorService.set(ERRORS.LOGIN_ERROR.message, null, true);
            return EMPTY;
          }

          this.redirectToSSOLogin();
          return null;
        }

        return this.requestLogin(
          this.buildLoginParams(token, currentEmployeeId)
        );
      }),
      filter((data): boolean => !!data),
      tap((data): void => {
        this.saveLoginData(data);
      })
    );
  }

  public saveLoginData(data: any): void {
    const authenticationData = authenticationDataBuilder(data);

    this.storageService.set(STORAGE_KEY.TOKEN, authenticationData.token);
    this.storageService.set(
      STORAGE_KEY.EMPLOYEE_ID,
      authenticationData.employeeId
    );
  }

  private requestLogin(params: any): Observable<any> {
    const authenticationUrl = API_ROUTE.AUTHENTICATION;
    return this.http.post<any>(
      `${authenticationUrl}`,
      params,
      this.httpOptions
    );
  }
}
